import { useHomeBanner } from '@/hooks/useHomeBanner';
import { BannerSlider } from './components/BannerSlider';
import { PromoMarquee } from '@/components/common/PromoMarquee';
import { CasinoTabs } from './components/CasinoTabs';
import SportsCards from './components/SportsCards';
import SportsSidebar from './components/SportsSidebar';
import { useMemo, useState } from 'react';
import { useGetCasinoGamesGroupedQuery } from '@/store/api/casinoApi';
import CasinoGameGrid from './components/CasinoGameGrid';
import GameSessionModal from '@/components/common/GameSessionModal';
import Loader from '@/components/common/Loader';
import { useCasinoGameplay } from '@/hooks/useCasinoGameplay';
import type { CasinoGroupedGamesQueryParams } from '@/types/casino';
import {
  CASINO_GROUPED_LIMIT,
  groupCasinoGamesByCategory,
  normalizeCasinoCategoryKey,
} from '@/utils/casinoGrouping';
import { WelcomeBanner } from './components/WelcomeBanner';

type SportsTab = 'inplay' | 'today' | 'tomorrow';
type CasinoSortOption = NonNullable<CasinoGroupedGamesQueryParams['sortBy']>;

function HomePage() {
  const [activeTab, setActiveTab] = useState<SportsTab>('inplay');
  const [activeTopTab, setActiveTopTab] = useState('sports');
  const [casinoSort, setCasinoSort] = useState<CasinoSortOption>('CATALOG');
  const { sessionUrl, isModalOpen, sessionLoading, handleGameClick, closeModal } =
    useCasinoGameplay();
  const {
    showWelcomeBanner,
    setShowWelcomeBanner,
    displayHomeBanner,
    displayWelcomeBanner,
    isBannersLoading,
  } = useHomeBanner();

  const { data: casinoGames, isLoading } = useGetCasinoGamesGroupedQuery({
    limitPerCategory: CASINO_GROUPED_LIMIT,
    sortBy: casinoSort,
  });

  const groupedCasinoSections = useMemo(() => {
    if (activeTopTab === 'sports') return [];

    const selectedCategory = casinoGames?.categories.find(
      (category) =>
        normalizeCasinoCategoryKey(category.category) === normalizeCasinoCategoryKey(activeTopTab)
    );

    if (!selectedCategory) return [];

    return groupCasinoGamesByCategory(selectedCategory.games);
  }, [activeTopTab, casinoGames]);

  const hasWelcomeBanner = useMemo(() => {
    return displayWelcomeBanner.length > 0;
  }, [displayWelcomeBanner]);

  return (
    <div className="text-white animate-fade-in">
      {sessionLoading && (
        <Loader className="fixed z-[70] flex items-center justify-center bg-black/50" />
      )}

      <BannerSlider
        banners={displayHomeBanner}
        className={'aspect-[7.5/2.8]'}
        isLoading={isBannersLoading}
      />
      <PromoMarquee />
      <CasinoTabs
        onChange={(value) => {
          setActiveTopTab(value);
          setCasinoSort('CATALOG');
        }}
      />
      <div className="grid grid-cols-[4.125rem_1fr] gap-2 px-2.5 py-2">
        <SportsSidebar
          variant={activeTopTab === 'sports' ? 'sports' : 'casino'}
          activeTab={activeTopTab === 'sports' ? activeTab : casinoSort}
          onTabChange={(tab) => {
            if (activeTopTab === 'sports') {
              if (tab === 'inplay' || tab === 'today' || tab === 'tomorrow') setActiveTab(tab);
              return;
            }

            setCasinoSort(tab as CasinoSortOption);
          }}
        />

        {activeTopTab === 'sports' ? (
          <SportsCards type={activeTab} />
        ) : (
          <CasinoGameGrid
            sections={groupedCasinoSections}
            isLoading={isLoading}
            onGameClick={handleGameClick}
          />
        )}
      </div>

      <GameSessionModal url={sessionUrl} isOpen={isModalOpen} onClose={closeModal} />

      {showWelcomeBanner && hasWelcomeBanner && (
        <WelcomeBanner
          setShowWelcomeBanner={setShowWelcomeBanner}
          banners={displayWelcomeBanner}
          isLoading={isBannersLoading}
        />
      )}
    </div>
  );
}

export default HomePage;
